{
  "title": "Getting started: platform admin first-time guide",
  "description": "From gaining super_admin access to day-to-day governance, a quick tour of a platform admin's complete first-time experience on NAPAICP.",
  "headings": [
    "What you'll accomplish",
    "Applies to",
    "Before you start",
    "Steps",
    "After this",
    "FAQ"
  ],
  "summary": "After reading this, you'll know how the first super_admin is created, the platform console's four-group navigation, which page to check day-to-day for overall governance status, and how this differs from managing a single competition.",
  "roleNote": "Platform admin (super_admin)",
  "prerequisites": [
    "Signed in with a verified email",
    "Already hold the super_admin global role (see \"Getting the first super_admin\" below)"
  ],
  "pathLabel": "/platform (visible only to super_admin)",
  "steps": [
    {
      "title": "1. Getting the first super_admin",
      "description": "Only when the platform has no active super_admin at all can a one-time, Bootstrap-credential-protected flow create the first one; once any super_admin is active, every subsequent grant is done by an existing super_admin on the Users page's \"Competitions & roles\" tab, never through Bootstrap again."
    },
    {
      "title": "2. The platform console's four nav groups",
      "description": "Under /platform, the sidebar is fixed into four groups: Platform Overview, People & Permissions (users, judge governance, admins, account erasure requests), Competition Governance (competitions, competition requests, discovery categories), and Security & Audit (audit log). This area is not a second source of truth for users/competitions/judges; it's a cross-competition governance view over the authoritative data."
    },
    {
      "title": "3. What to check day-to-day: Platform Overview",
      "description": "The top of Platform Overview shows a \"Needs your attention\" card summarizing pending competition requests and any risk of being down to just 1 active super_admin; below it are two aggregate metric cards (\"Platform scale\" and \"Governance status\"), each clickable straight into the matching filtered list."
    },
    {
      "title": "4. Reviewing competition hosting requests",
      "description": "Review requests to host a competition under \"Competition Governance\" → \"Competition requests\"; approval automatically and idempotently provisions the real competition and sets the requester as its first competition_admin in one step."
    },
    {
      "title": "5. Managing users and judge governance",
      "description": "\"People & Permissions\" lets you search users, see their per-competition role summary, and grant or revoke roles (revoking requires a reason and never deletes existing team/judging relationship history), plus handle cross-competition judge governance."
    },
    {
      "title": "6. Where the platform admin's scope ends",
      "description": "Platform admin governance covers cross-competition users, competition relationships, judge governance, and platform-level audit. Settings, day-to-day operations, data, review, and permissions within a single competition remain that competition's own competition_admin's responsibility; platform admin never bypasses or replaces competition-scoped authorization."
    }
  ],
  "afterward": [
    "You can return to Platform Overview any time for the current overall governance state",
    "Every user suspension, role grant/revocation, and super_admin change is audited, visible under \"Security & Audit\"",
    "The platform must have at least one active super_admin at all times; any action that would drop that count to zero is rejected server-side"
  ],
  "nextSteps": "For full rules, per-group operations, and constraints, see the Platform Admin manual; for a quick day-to-day read on overall governance, see Platform Overview.",
  "faq": [
    {
      "q": "Can I make myself a super_admin?",
      "a": "Only when the platform genuinely has zero active super_admin can the one-time, protected Bootstrap flow create the first one. After that, every new super_admin grant must be performed by an existing super_admin; there is no self-service path."
    },
    {
      "q": "Can a platform admin directly edit a single competition's internal settings?",
      "a": "No. The platform console is a cross-competition governance view, not a second writable source of competition data; settings, operations, and review within a competition remain the responsibility of that competition's own competition_admin/staff."
    },
    {
      "q": "Which page should I check first after opening the platform console?",
      "a": "Start with Platform Overview to see pending governance tasks plus scale and governance-status metrics in one place; detailed user or competition actions happen in their own respective groups."
    }
  ]
}
